import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Circle, 
  Square, 
  ArrowRight, 
  Pencil, 
  Undo2, 
  Trash2,
  Check,
  MousePointer2
} from 'lucide-react';

const annotationColor = '#ff3333';
const lineWidth = 3;

// Drawing helper functions (outside component to avoid recreating)
const drawArrow = (ctx, start, end) => {
  const headLength = 15;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const angle = Math.atan2(dy, dx);

  ctx.beginPath();
  ctx.moveTo(start.x, start.y);
  ctx.lineTo(end.x, end.y);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(end.x, end.y);
  ctx.lineTo(
    end.x - headLength * Math.cos(angle - Math.PI / 6),
    end.y - headLength * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    end.x - headLength * Math.cos(angle + Math.PI / 6),
    end.y - headLength * Math.sin(angle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();
};

const drawCircle = (ctx, start, end) => {
  const radiusX = Math.abs(end.x - start.x) / 2;
  const radiusY = Math.abs(end.y - start.y) / 2;
  const centerX = (start.x + end.x) / 2;
  const centerY = (start.y + end.y) / 2;

  ctx.beginPath();
  ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
  ctx.stroke();
};

const drawRectangle = (ctx, start, end) => {
  ctx.beginPath();
  ctx.rect(start.x, start.y, end.x - start.x, end.y - start.y);
  ctx.stroke();
};

const drawFreehand = (ctx, path) => {
  if (path.length < 2) return;

  ctx.beginPath();
  ctx.moveTo(path[0].x, path[0].y);
  for (let i = 1; i < path.length; i++) {
    ctx.lineTo(path[i].x, path[i].y);
  }
  ctx.stroke();
};

const drawPointer = (ctx, position) => {
  const size = 20;
  
  ctx.beginPath();
  ctx.arc(position.x, position.y, size, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(255, 51, 51, 0.3)';
  ctx.fill();
  ctx.strokeStyle = annotationColor;
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(position.x, position.y, 5, 0, 2 * Math.PI);
  ctx.fillStyle = annotationColor;
  ctx.fill();
};

const drawAnnotation = (ctx, annotation) => {
  ctx.strokeStyle = annotationColor;
  ctx.fillStyle = annotationColor;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (annotation.type) {
    case 'arrow':
      drawArrow(ctx, annotation.start, annotation.end);
      break;
    case 'circle':
      drawCircle(ctx, annotation.start, annotation.end);
      break;
    case 'rectangle':
      drawRectangle(ctx, annotation.start, annotation.end);
      break;
    case 'freehand':
      drawFreehand(ctx, annotation.path);
      break;
    case 'pointer':
      drawPointer(ctx, annotation.position);
      break;
    default:
      break;
  }
};

const ScreenshotAnnotator = ({ screenshotData, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [tool, setTool] = useState('rectangle'); // Default to rectangle
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [annotations, setAnnotations] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load image when screenshot data changes
  useEffect(() => {
    if (!screenshotData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const container = containerRef.current;
      const maxWidth = container?.clientWidth || 600;
      const maxHeight = 400;
      
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = (maxHeight / height) * width;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;
      
      ctx.drawImage(img, 0, 0, width, height);
      setImageLoaded(true);
    };

    img.src = screenshotData;
  }, [screenshotData]);

  // Redraw canvas with all annotations
  const redrawCanvas = useCallback(() => {
    if (!canvasRef.current || !screenshotData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      annotations.forEach(annotation => {
        drawAnnotation(ctx, annotation);
      });
    };

    img.src = screenshotData;
  }, [screenshotData, annotations]);

  useEffect(() => {
    redrawCanvas();
  }, [annotations, redrawCanvas]);

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleMouseDown = (e) => {
    const pos = getMousePos(e);
    setIsDrawing(true);
    setStartPos(pos);

    if (tool === 'freehand') {
      setCurrentPath([pos]);
    } else if (tool === 'pointer') {
      setAnnotations([...annotations, { type: 'pointer', position: pos }]);
      setIsDrawing(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;

    const pos = getMousePos(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    redrawCanvas();

    if (tool === 'freehand') {
      setCurrentPath(prev => [...prev, pos]);
      ctx.strokeStyle = annotationColor;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      drawFreehand(ctx, [...currentPath, pos]);
    } else if (tool !== 'pointer') {
      ctx.strokeStyle = annotationColor;
      ctx.fillStyle = annotationColor;
      ctx.lineWidth = lineWidth;

      if (tool === 'arrow') {
        drawArrow(ctx, startPos, pos);
      } else if (tool === 'circle') {
        drawCircle(ctx, startPos, pos);
      } else if (tool === 'rectangle') {
        drawRectangle(ctx, startPos, pos);
      }
    }
  };

  const handleMouseUp = (e) => {
    if (!isDrawing) return;

    const pos = getMousePos(e);
    setIsDrawing(false);

    if (tool === 'freehand' && currentPath.length > 1) {
      setAnnotations([...annotations, { type: 'freehand', path: currentPath }]);
      setCurrentPath([]);
    } else if (tool !== 'pointer' && tool !== 'freehand') {
      if (Math.abs(pos.x - startPos.x) > 5 || Math.abs(pos.y - startPos.y) > 5) {
        setAnnotations([...annotations, { type: tool, start: startPos, end: pos }]);
      }
    }
  };

  const handleUndo = () => {
    setAnnotations(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setAnnotations([]);
  };

  const handleSave = () => {
    if (!canvasRef.current) return;
    const annotatedData = canvasRef.current.toDataURL('image/jpeg', 0.8);
    onSave(annotatedData);
  };

  const tools = [
    { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
    { id: 'circle', icon: Circle, label: 'Circle' },
    { id: 'rectangle', icon: Square, label: 'Rectangle' },
    { id: 'freehand', icon: Pencil, label: 'Draw' },
    { id: 'pointer', icon: MousePointer2, label: 'Point' },
  ];

  if (!screenshotData) {
    return (
      <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
        <p className="text-gray-500">No screenshot available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" ref={containerRef}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {tools.map(({ id, icon: Icon, label }) => (
            <Button
              key={id}
              variant={tool === id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTool(id)}
              title={label}
              className="h-8 w-8 p-0"
            >
              <Icon className="h-4 w-4" />
            </Button>
          ))}
        </div>
        
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndo}
            disabled={annotations.length === 0}
            title="Undo"
            className="h-8 w-8 p-0"
          >
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            disabled={annotations.length === 0}
            title="Clear all"
            className="h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <p className="text-xs text-gray-500">
        Use the tools above to mark the issue on the screenshot. Click and drag to draw.
      </p>

      {/* Canvas */}
      <div className="border rounded-lg overflow-hidden bg-gray-100">
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => setIsDrawing(false)}
          className="cursor-crosshair w-full"
          style={{ maxHeight: '400px', display: imageLoaded ? 'block' : 'none' }}
        />
        {!imageLoaded && (
          <div className="h-48 flex items-center justify-center">
            <p className="text-gray-500">Loading screenshot...</p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave}>
          <Check className="h-4 w-4 mr-1" />
          Save Annotations
        </Button>
      </div>
    </div>
  );
};

export default ScreenshotAnnotator;
